import React, { useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import theme from "../styles/theme";
import { TEXT_CONSTANTS } from "../constants/textConstants";
import { authService } from "../services/authService";
import "./LoginPage.css";

const PORTAL_FEATURES = [
  {
    icon: DashboardCustomizeOutlinedIcon,
    title: "Internal projects & tools",
    description: "Access every internal application from one place",
  },
  {
    icon: GroupsOutlinedIcon,
    title: "Team directory & updates",
    description: "Discover colleagues and stay connected",
  },
  {
    icon: CampaignOutlinedIcon,
    title: "Organization news & resources",
    description: "Company announcements, links, and knowledge",
  },
];

const PORTAL_PILLS = ["Projects", "People", "News", "Resources"];

const SSOButton = ({ icon, label, onClick, loading }) => (
  <button
    type="button"
    className="login-sso-btn"
    onClick={onClick}
    disabled={loading}
    aria-label={label}
  >
    {loading ? (
      <CircularProgress size={20} sx={{ color: "#64748b" }} />
    ) : (
      <>
        <img src={icon} alt="" className="login-sso-icon" aria-hidden="true" />
        {label}
      </>
    )}
  </button>
);

const LoginPage = () => {
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [error, setError] = useState("");

  const handleSignIn = async (provider, signInFn) => {
    setLoadingProvider(provider);
    setError("");
    try {
      await signInFn();
    } catch {
      setError(`${provider} sign-in failed. Please try again.`);
      setLoadingProvider(null);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="login-page" component="main">
        <Box className="login-page-bg" aria-hidden="true" />
        <Box className="login-page-grid" aria-hidden="true" />

        <Box className="login-shell">
          <Box className="login-hero">
            <Box className="login-hero-bg" aria-hidden="true" />
            <Box className="login-hero-overlay" aria-hidden="true" />
            <Box className="login-hero-glow" aria-hidden="true" />

            <Box className="login-hero-content">
              <Box className="login-brand-row">
                
                <span className="login-brand-badge">
                  <span className="login-brand-dot" />
                  {TEXT_CONSTANTS.AUTH.LOGIN_SUBTITLE}
                </span>
              </Box>

              <h1 className="login-hero-title">
                Welcome to{" "}
                <span className="login-hero-title-accent">
                  {TEXT_CONSTANTS.AUTH.LOGIN_TITLE}
                </span>
              </h1>

              <p className="login-hero-subtitle">
                {TEXT_CONSTANTS.APP.DESCRIPTION} Connect, collaborate, and stay
                in the loop with everything Airo.
              </p>

              <Box className="login-pills">
                {PORTAL_PILLS.map((pill) => (
                  <span key={pill} className="login-pill">
                    {pill}
                  </span>
                ))}
              </Box>

              <Box className="login-features">
                {PORTAL_FEATURES.map(({ icon: Icon, title, description }) => (
                  <Box key={title} className="login-feature">
                    <Box className="login-feature-icon">
                      <Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <p className="login-feature-title">{title}</p>
                      <p className="login-feature-desc">{description}</p>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <p className="login-hero-footer">
              © {new Date().getFullYear()} Airo Digital Labs · Internal use only
            </p>
          </Box>

          <Box className="login-panel">
            <Box className="login-panel-inner">
              <Box className="login-mobile-brand">
                <img
                  src="/kyc-logo.png"
                  alt="Airo"
                  className="login-brand-logo"
                />
                <p className="login-mobile-title">
                  {TEXT_CONSTANTS.AUTH.LOGIN_TITLE}
                </p>
                <p className="login-mobile-subtitle">
                  {TEXT_CONSTANTS.AUTH.LOGIN_SUBTITLE}
                </p>
              </Box>

              <Box className="login-panel-logo-wrap">
                <img
                  src="https://airolabs.ai/wp-content/uploads/2026/05/airo-logo.png"
                  alt="Airo"
                  className="login-panel-logo"
                />
              </Box>

              <h2 className="login-panel-heading">Sign in to continue</h2>
              <p className="login-panel-subheading">
                Use your Airo work account to access the intranet portal.
              </p>

              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <SSOButton
                icon="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                label="Continue with Google"
                loading={loadingProvider === "Google"}
                onClick={() =>
                  handleSignIn("Google", authService.loginWithGoogle)
                }
              />
              <SSOButton
                icon="https://img.icons8.com/color/48/microsoft.png"
                label="Continue with Microsoft"
                loading={loadingProvider === "Microsoft"}
                onClick={() =>
                  handleSignIn("Microsoft", authService.loginWithMicrosoft)
                }
              />

              <Box className="login-divider">
                <Box className="login-divider-line" />
                <span className="login-divider-text">Secure access</span>
                <Box className="login-divider-line" />
              </Box>

              <Box className="login-trust-row">
                <LockOutlinedIcon className="login-trust-icon" />
                <p className="login-trust-text">
                  Authorized Airo employees only
                </p>
              </Box>

              <p className="login-panel-note">
                Contact IT support if you need access.
              </p>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LoginPage;
